import { endOfYesterday, startOfMonth, startOfWeek, subMonths } from "date-fns";
import { CheckSquare, Clock, Star, Tag, TrendingUp, Users, X } from "lucide-react";
import {
  useGetIdentity,
  useGetList,
  useListContext,
  useTranslate,
} from "ra-core";
import { ToggleFilterButton } from "@/components/admin/toggle-filter-button";
import { Badge } from "@/components/ui/badge";

import { FilterCategory } from "../filters/FilterCategory";
import { TagToggleFilterButton, ActiveTagFilterButton } from "../filters/TagToggleFilterButton";
import { TaskTypeToggleFilterButton, ActiveTaskTypeFilterButton } from "../filters/TaskTypeToggleFilterButton";
import { Status } from "../misc/Status";
import { useConfigurationContext } from "../root/ConfigurationContext";
import { defaultTaskTypes } from "../root/defaultConfiguration";
import { ResponsiveFilters } from "../misc/ResponsiveFilters";
import { useIsMobile } from "@/hooks/use-mobile";
import { ActiveFilterButton } from "../misc/ActiveFilterButton";

// Task type groupings — matches the two-column panel in contact task selector (§10.5)
const TASK_STAGE_GROUPS = [
  {
    label: "联系人待办",
    values: [
      "send-dev-email", "send-catalog", "follow-inquiry", "confirm-requirements",
      "send-sample", "follow-sample-receipt", "follow-sample-approval",
      "confirm-receipt", "seasonal-greeting", "repurchase-followup",
    ],
  },
  {
    label: "订单待办",
    values: [
      "send-quotation", "follow-quotation", "negotiate-price",
      "send-pi", "follow-deposit", "follow-production",
      "arrange-inspection", "follow-balance", "follow-shipment",
      "handle-complaint",
    ],
  },
];

export const ContactListFilter = () => {
  const { noteStatuses } = useConfigurationContext();
  // Always use defaultTaskTypes for filter groups — context may have stale/old values from DB
  const taskTypes = defaultTaskTypes;
  const isMobile = useIsMobile();
  const { identity } = useGetIdentity();
  const translate = useTranslate();
  const { data } = useGetList("tags", {
    pagination: { page: 1, perPage: 200 },
    sort: { field: "sort_order", order: "ASC" },
  });

  // Group tags by category; tags without a category go into "其他"
  const tagsByCategory = (data ?? []).reduce<Record<string, typeof data>>((acc, tag) => {
    const cat = (tag as any).category ?? "其他";
    if (!acc[cat]) acc[cat] = [];
    acc[cat]!.push(tag);
    return acc;
  }, {});
  const categories = Object.keys(tagsByCategory);

  return (
    <ResponsiveFilters
      searchInput={{
        placeholder: translate("resources.contacts.filters.search"),
      }}
    >
      <FilterCategory
        label="resources.contacts.fields.last_seen"
        icon={<Clock />}
        collapsible
      >
        <ToggleFilterButton
          className="w-auto md:w-full justify-between h-10 md:h-8"
          label="resources.contacts.filters.today"
          value={{
            "last_seen@gte": endOfYesterday().toISOString(),
            "last_seen@lte": undefined,
          }}
          size={isMobile ? "lg" : undefined}
        />
        <ToggleFilterButton
          className="w-auto md:w-full justify-between h-10 md:h-8"
          label="resources.contacts.filters.this_week"
          value={{
            "last_seen@gte": startOfWeek(new Date()).toISOString(),
            "last_seen@lte": undefined,
          }}
          size={isMobile ? "lg" : undefined}
        />
        <ToggleFilterButton
          className="w-auto md:w-full justify-between h-10 md:h-8"
          label="resources.contacts.filters.before_this_week"
          value={{
            "last_seen@gte": undefined,
            "last_seen@lte": startOfWeek(new Date()).toISOString(),
          }}
          size={isMobile ? "lg" : undefined}
        />
        <ToggleFilterButton
          className="w-auto md:w-full justify-between h-10 md:h-8"
          label="resources.contacts.filters.before_this_month"
          value={{
            "last_seen@gte": undefined,
            "last_seen@lte": startOfMonth(new Date()).toISOString(),
          }}
          size={isMobile ? "lg" : undefined}
        />
        <ToggleFilterButton
          className="w-auto md:w-full justify-between h-10 md:h-8"
          label="resources.contacts.filters.before_last_month"
          value={{
            "last_seen@gte": undefined,
            "last_seen@lte": subMonths(
              startOfMonth(new Date()),
              1,
            ).toISOString(),
          }}
          size={isMobile ? "lg" : undefined}
        />
      </FilterCategory>

      <FilterCategory
        label="resources.notes.fields.status"
        icon={<TrendingUp />}
        collapsible
      >
        {noteStatuses.map((status) => (
          <ToggleFilterButton
            key={status.value}
            className="w-auto md:w-full justify-between h-10 md:h-8"
            label={
              <span>
                {status.label} <Status status={status.value} />
              </span>
            }
            value={{ status: status.value }}
            size={isMobile ? "lg" : undefined}
          />
        ))}
      </FilterCategory>

      <FilterCategory label="客户画像" icon={<Tag />} collapsible defaultCollapsed>
        {categories.map((cat) => (
          <FilterCategory key={cat} label={cat} collapsible defaultCollapsed>
            {tagsByCategory[cat]!.map((record) => (
              <TagToggleFilterButton
                className="w-auto md:w-full justify-between h-10 md:h-8"
                key={record.id}
                tagId={record.id as number}
                label={
                  <span
                    className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium text-gray-800"
                    style={{ backgroundColor: record?.color }}
                    title={(record as any).description}
                  >
                    {record?.name}
                  </span>
                }
                size={isMobile ? "lg" : undefined}
              />
            ))}
          </FilterCategory>
        ))}
      </FilterCategory>

      <FilterCategory
        icon={<CheckSquare />}
        label="resources.contacts.filters.tasks"
        collapsible
      >
        {TASK_STAGE_GROUPS.map((stage) => {
          const stageTypes = taskTypes?.filter((t) => stage.values.includes(t.value)) ?? [];
          if (!stageTypes.length) return null;
          return (
            <FilterCategory key={stage.label} label={stage.label} collapsible defaultCollapsed>
              {stageTypes.map((t) => (
                <TaskTypeToggleFilterButton
                  key={t.value}
                  className="w-auto md:w-full justify-between h-10 md:h-8"
                  taskType={t.value}
                  label={t.label}
                  size={isMobile ? "lg" : undefined}
                />
              ))}
            </FilterCategory>
          );
        })}
      </FilterCategory>

      <FilterCategory
        icon={<Users />}
        label="resources.contacts.fields.sales_id"
        collapsible
      >
        <ToggleFilterButton
          className="w-full justify-between h-10 md:h-8"
          label="crm.common.me"
          value={{ sales_id: identity?.id }}
          size={isMobile ? "lg" : undefined}
        />
      </FilterCategory>

      <FilterCategory
        icon={<Star />}
        label="resources.contacts.filters.vip_level"
        collapsible
      >
        <ToggleFilterButton
          className="w-auto md:w-full justify-between h-10 md:h-8"
          label={<span className="flex items-center gap-1.5">🏅 注册客户 Registered</span>}
          value={{ vip_level: "standard" }}
          size={isMobile ? "lg" : undefined}
        />
        <ToggleFilterButton
          className="w-auto md:w-full justify-between h-10 md:h-8"
          label={<span className="flex items-center gap-1.5">🥈 银牌 Silver</span>}
          value={{ vip_level: "silver" }}
          size={isMobile ? "lg" : undefined}
        />
        <ToggleFilterButton
          className="w-auto md:w-full justify-between h-10 md:h-8"
          label={<span className="flex items-center gap-1.5">🥇 金牌 Gold</span>}
          value={{ vip_level: "gold" }}
          size={isMobile ? "lg" : undefined}
        />
        <ToggleFilterButton
          className="w-auto md:w-full justify-between h-10 md:h-8"
          label={<span className="flex items-center gap-1.5">💎 铂金 Platinum</span>}
          value={{ vip_level: "platinum" }}
          size={isMobile ? "lg" : undefined}
        />
        <ToggleFilterButton
          className="w-auto md:w-full justify-between h-10 md:h-8"
          label={<span className="flex items-center gap-1.5">👑 钻石 Diamond</span>}
          value={{ vip_level: "diamond" }}
          size={isMobile ? "lg" : undefined}
        />
      </FilterCategory>
    </ResponsiveFilters>
  );
};

export const ContactListFilterSummary = () => {
  const { noteStatuses } = useConfigurationContext();
  const taskTypes = defaultTaskTypes;
  const { identity } = useGetIdentity();
  const { data } = useGetList("tags", {
    pagination: { page: 1, perPage: 200 },
    sort: { field: "sort_order", order: "ASC" },
  });
  const { filterValues, setFilters } = useListContext();
  const hasFilters = !!Object.entries(filterValues || {}).filter(
    ([key]) => key !== "q",
  ).length;

  if (!hasFilters) {
    return null;
  }

  return (
    <div className="flex flex-wrap items-start mb-4 gap-1">
      <ActiveFilterButton
        className="w-auto justify-between h-8"
        label="resources.contacts.filters.today"
        value={{
          "last_seen@gte": endOfYesterday().toISOString(),
          "last_seen@lte": undefined,
        }}
      />
      <ActiveFilterButton
        className="w-auto justify-between h-8"
        label="resources.contacts.filters.this_week"
        value={{
          "last_seen@gte": startOfWeek(new Date()).toISOString(),
          "last_seen@lte": undefined,
        }}
      />
      <ActiveFilterButton
        className="w-auto justify-between h-8"
        label="resources.contacts.filters.before_this_week"
        value={{
          "last_seen@gte": undefined,
          "last_seen@lte": startOfWeek(new Date()).toISOString(),
        }}
      />
      <ActiveFilterButton
        className="w-auto justify-between h-8"
        label="resources.contacts.filters.before_this_month"
        value={{
          "last_seen@gte": undefined,
          "last_seen@lte": startOfMonth(new Date()).toISOString(),
        }}
      />
      <ActiveFilterButton
        className="w-auto justify-between h-8"
        label="resources.contacts.filters.before_last_month"
        value={{
          "last_seen@gte": undefined,
          "last_seen@lte": subMonths(startOfMonth(new Date()), 1).toISOString(),
        }}
      />

      {noteStatuses.map((status) => (
        <ActiveFilterButton
          key={status.value}
          className="w-auto justify-between h-8"
          label={
            <span>
              {status.label} <Status status={status.value} />
            </span>
          }
          value={{ status: status.value }}
        />
      ))}

      {data &&
        data.map((record) => (
          <ActiveTagFilterButton
            className="w-auto justify-between h-8"
            key={record.id}
            tagId={record.id as number}
            label={
              <Badge
                variant="secondary"
                className="text-black text-sm md:text-xs font-normal cursor-pointer"
                style={{
                  backgroundColor: record?.color,
                }}
              >
                {record?.name}
              </Badge>
            }
          />
        ))}

      {taskTypes?.map((t) => (
        <ActiveTaskTypeFilterButton
          key={t.value}
          className="w-auto justify-between h-8"
          taskType={t.value}
          label={t.label}
        />
      ))}

      <ActiveFilterButton
        className="w-auto justify-between h-8"
        label="resources.contacts.filters.managed_by_me"
        value={{ sales_id: identity?.id }}
      />

      <ActiveFilterButton
        className="w-auto justify-between h-8"
        label={<span className="flex items-center gap-1">🏅 注册客户</span>}
        value={{ vip_level: "standard" }}
      />
      <ActiveFilterButton
        className="w-auto justify-between h-8"
        label={<span className="flex items-center gap-1">🥈 银牌</span>}
        value={{ vip_level: "silver" }}
      />
      <ActiveFilterButton
        className="w-auto justify-between h-8"
        label={<span className="flex items-center gap-1">🥇 金牌</span>}
        value={{ vip_level: "gold" }}
      />
      <ActiveFilterButton
        className="w-auto justify-between h-8"
        label={<span className="flex items-center gap-1">💎 铂金</span>}
        value={{ vip_level: "platinum" }}
      />
      <ActiveFilterButton
        className="w-auto justify-between h-8"
        label={<span className="flex items-center gap-1">👑 钻石</span>}
        value={{ vip_level: "diamond" }}
      />

      <button
        type="button"
        onClick={() => setFilters({}, [])}
        className="ml-1 h-8 px-2 text-xs text-muted-foreground hover:text-destructive flex items-center gap-1 rounded border border-input hover:border-destructive transition-colors"
      >
        <X className="w-3 h-3" />
        清空筛选
      </button>
    </div>
  );
};
